/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { gql } from '@apollo/client';
import { CONTEXT_LEARNING_INFO } from '../fragments/learning';

export const LIST_DEPARTMENT_LEARNED_ANSWERS = gql`
  ${CONTEXT_LEARNING_INFO}
  query listDepartmentLearnedAnswers(
    $id: ID!
    $limit: Int
    $nextToken: String
  ) {
    getDepartment(id: $id) {
      learnedAnswers(limit: $limit, nextToken: $nextToken) {
        nextToken
        items {
          id
          questionFilter {
            stepPath
            questionType
            lexicalPath
          }
          action {
            _
            ... on RetryStepAction {
              minimumDelay
              timeout
            }
            ... on UseConceptAction {
              conceptId
            }
            ... on UseLiteralValueAction {
              literalValue
              choices
            }
            ... on UseTechniqueAction {
              technique
            }
          }
          createdAt
          createdBy
          deletedAt
          deletedBy
          procedureId
          procedureName
          stage
          procedure {
            id
            name
            knowledgeId
            stage
          }
          contextValue
          contextMetadata
          contextualLearningInfos {
            items {
              ...ContextualLearningInfoFragment
            }
          }
        }
      }
    }
  }
`;

export const LIST_PROCEDURE_LEARNED_ANSWERS = gql`
  ${CONTEXT_LEARNING_INFO}
  query ListLearnedAnswersByProcedure(
    $procedureId: ID!
    $departmentId: ID!
    $limit: Int = 100
    $nextToken: String
    $filters: LearningsFilters
  ) {
    listLearnedAnswersByProcedure(
      procedureId: $procedureId
      departmentId: $departmentId
      limit: $limit
      nextToken: $nextToken
      filters: $filters
    ) {
      items {
        id
        stage
        questionFilter {
          stepPath
          questionType
          lexicalPath
        }
        action {
          ... on RetryStepAction {
            minimumDelay
            timeout
          }
          ... on UseConceptAction {
            conceptId
          }
          ... on UseLiteralValueAction {
            literalValue
            choices
          }
          ... on UseTechniqueAction {
            technique
          }
        }
        createdAt
        createdBy
        deletedAt
        deletedBy
        updatedAt
        procedure {
          id
          name
          stage
        }
        contextValue
        contextMetadata
        stage
        contextualLearningInfos {
          items {
            ...ContextualLearningInfoFragment
          }
        }
      }
      nextToken
    }
  }
`;

export const LIST_LEARNED_ANSWER_SUGGESTIONS = gql`
  query GetLearnedAnswerSuggestions($input: LearnedAnswerSuggestionsInput!) {
    getLearnedAnswerSuggestions(input: $input) {
      items {
        id
        createdAt
        createdBy
        deletedAt
        deletedBy
        procedureId
        procedureName
        contextValue
        contextMetadata
        questionFilter {
          stepPath
          questionType
          lexicalPath
        }
        action {
          _

          ... on RetryStepAction {
            minimumDelay
            timeout
          }
          ... on UseConceptAction {
            conceptId
          }
          ... on UseLiteralValueAction {
            literalValue
            choices
          }
          ... on UseTechniqueAction {
            technique
          }
        }
      }
      nextToken
    }
  }
`;

export const GET_CONTEXTUAL_LEARNING_INFO_BY_REQUEST = gql`
  query GetContextualLearningInfoByRequest($requestId: ID!) {
    getContextualLearningInfoByRequest(requestId: $requestId) {
      items {
        id
        createdAt
        createdBy
        procedureId
        learningId
        requestId
        procedureName
        contextMetadata
      }
    }
  }
`;

export const GET_LEARNED_ANSWER_BY_REQUEST_ANSWER_ID = gql`
  query GetLearnedAnswerByRequestAnswerId($requestAnswerId: ID!) {
    getLearnedAnswerByRequestAnswerId(requestAnswerId: $requestAnswerId) {
      id
      questionFilter {
        stepPath
        questionType
        lexicalPath
      }
      action {
        _
        ... on ReplaceStepAction {
          newSteps
        }
        ... on RetryStepAction {
          minimumDelay
          timeout
        }
        ... on UseConceptAction {
          conceptId
        }
        ... on UseLiteralValueAction {
          literalValue
          choices
        }
        ... on UseOauth2Action {
          accessToken
          refreshToken
        }
        ... on UseTechniqueAction {
          technique
        }
      }
      originalAnswerId
      createdAt
      createdBy
      deletedAt
      deletedBy
      procedureId
      procedureName
      procedure {
        id
        knowledgeId
        departmentId
        text
        name
        owner
        language
        public
        bookReference
        title
        version
      }
      contextValue
      contextMetadata
    }
  }
`;

export const GET_PROCEDURES_LEARNINGS_SUMMARY = gql`
  query getLearnedAnswersSummaryByProcedureIds(
    $departmentId: ID!
    $procedureIds: [ID!]!
    $sinceTimestamp: String
  ) {
    getLearnedAnswersSummaryByProcedureIds(
      departmentId: $departmentId
      procedureIds: $procedureIds
      sinceTimestamp: $sinceTimestamp
    ) {
      items {
        procedureId
        draftLearningsCreatedCount
        publishedLearningsCreatedCount
        draftLearningsUpdatedCount
        publishedLearningsUpdatedCount
      }
    }
  }
`;

export const GET_LEARNED_ANSWER = gql`
  query GetLearnedAnswer($id: ID!) {
    getLearnedAnswer(id: $id) {
      id
      questionFilter {
        stepPath
        questionType
        lexicalPath
      }
      action {
        _
        ... on RetryStepAction {
          minimumDelay
          timeout
        }
        ... on UseConceptAction {
          conceptId
        }
        ... on UseLiteralValueAction {
          literalValue
          choices
        }
        ... on UseTechniqueAction {
          technique
        }
      }
      createdAt
      createdBy
      deletedAt
      deletedBy
      stage
      procedure {
        id
        name
        knowledgeId
      }
      contextValue
      contextMetadata
    }
  }
`;
